// src/services/api/salesReport.js
import { apiClient } from './base';

/**
 * Get filter options for sales reports
 * @returns {Promise<Object>} Filter options (shops, statuses, date presets)
 */
export const getSalesReportFilters = async () => {
  try {
    const response = await apiClient.get('/sales-reports/filters');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get sales summary data for charts and cards
 * @param {Object} params - Query parameters
 * @param {string} params.groupByDate - 'date' | 'week' | 'month'
 * @param {string} params.filterByDate - 'createdAt' | 'dispatchedDate'
 * @param {boolean} params.groupByProduct - Group by product instead of time
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.statusIds - Comma-separated status IDs
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Summary data
 */
export const getSalesSummary = async (params) => {
  try {
    const response = await apiClient.get('/sales-reports/summary', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get detailed sales data for tables
 * @param {Object} params - Query parameters
 * @param {string} params.groupByDate - 'date' | 'week' | 'month'
 * @param {string} params.filterByDate - 'createdAt' | 'dispatchedDate'
 * @param {boolean} params.groupByProduct - Group by product instead of time
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.statusIds - Comma-separated status IDs
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.orderBy - Field to order by
 * @param {string} params.orderDirection - 'ASC' | 'DESC'
 * @returns {Promise<Object>} Detailed data with pagination
 */
export const getSalesDetails = async (params) => {
  try {
    const response = await apiClient.get('/sales-reports/details', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get dashboard overview metrics
 * @param {Object} params - Query parameters
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.dateRange - 'today' | 'week' | 'month' | 'custom'
 * @returns {Promise<Object>} Dashboard metrics with comparison data
 */
export const getDashboardOverview = async (params) => {
  try {
    const response = await apiClient.get('/sales-reports/dashboard-overview', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get top selling products
 * @param {Object} params - Query parameters
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.limit - Number of products to return (default 10)
 * @returns {Promise<Object>} Top products with sales data
 */
export const getTopProducts = async (params) => {
  try {
    const response = await apiClient.get('/sales-reports/top-products', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get sales trend over time
 * @param {Object} params - Query parameters
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.period - '7days' | '14days' | '30days'
 * @param {string} params.groupBy - 'date' | 'week'
 * @returns {Promise<Object>} Time series data with sales value, quantity, and order count
 */
export const getSalesTrend = async (params) => {
  try {
    const response = await apiClient.get('/sales-reports/trend', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};